import { ExternalLink } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import SectionCard from './SectionCard';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { GetCaseOverviewRes } from '@/api/types/queries';
import { Skeleton } from '@/components/atoms/Skeleton';

interface KeyDocumentsSectionProps {
  caseId: string | null;
  overview: GetCaseOverviewRes | undefined;
  overviewIsLoading: boolean;
}

export const KeyDocumentsSection = ({ caseId, overview, overviewIsLoading }: KeyDocumentsSectionProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const formatDocumentTitle = (title: string) => {
    if (title === null || title === undefined) return '';
    if (title.length > 35) {
      return title.slice(0, 35) + '...';
    }
    return title;
  };

  const handleDocumentClick = (docId: string) => {
    if (!caseId) return;
    trackCustomEvent(TrackingEvents.ClickRelevantDocsOnSummary);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('caseId', caseId);
    searchParams.set('docId', docId);
    searchParams.set('page', '0');
    navigate('/app/chronos/case-editor/data/documents?' + searchParams.toString());
  };

  const handleNavigateToDocuments = () => {
    navigate(`/app/chronos/case-editor/data/documents?caseId=${caseId}`);
  };
  return (
    <div className="lg:col-span-3">
      <SectionCard
        title="Most Relevant Documents"
        className="h-full"
        headerActions={
          <button
            onClick={() => {
              handleNavigateToDocuments();
              trackCustomEvent(TrackingEvents.ViewAllDocsOnSummary);
            }}
            className="inline-flex items-center gap-1 text-sm text-blue-500 underline hover:text-blue-700"
          >
            View All ({overview?.documents || 0})
            <ExternalLink className="h-3 w-3" />
          </button>
        }
      >
        {overviewIsLoading ? (
          <div className="flex h-full flex-col gap-1">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="flex cursor-pointer items-center justify-between rounded border border-gray-200 bg-white px-2 py-1 text-xs"
              >
                <Skeleton className="h-3 w-32 bg-gray-200" />
                <div className="flex items-center gap-1">
                  <Skeleton className="h-3 w-4 bg-gray-200" />
                  <Skeleton className="h-3 w-8 bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        ) : overview?.keyFactsByDoc?.length ? (
          <div className="flex h-full flex-col gap-1">
            {overview.keyFactsByDoc.slice(0, 3).map((doc, index) => (
              <div
                key={doc.documentId}
                className={`flex cursor-pointer items-center justify-between rounded border px-2 py-1 text-xs transition-all hover:scale-[1.02] hover:shadow-sm ${
                  index === 0 ? 'border-blue-600/40 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'
                }`}
                onClick={() => handleDocumentClick(doc.documentId)}
              >
                <span className="truncate text-gray-700 italic" title={doc.fileName}>
                  {formatDocumentTitle(doc.fileName)}
                </span>
                <span className="ml-2 text-xs text-gray-500">
                  <span className="font-semibold text-blue-500">{doc.count}</span> key facts
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-gray-400">No documents available</div>
        )}
      </SectionCard>
    </div>
  );
};
